<template>
    <h3 class="mt-5 mb-4">Retirement Advisers</h3>

    <p>Guiide can't give you financial advice. We can't give recommendations on where to invest your pension pots, what products are best for you, or how to best deal with more complicated tax issues.</p>
    <p>
        If you want advice, you can take this solely at retirement, or on an ongoing basis, where the adviser reviews everything each year with you and may make new recommendations.
        One off retirement advice may cost around 2 - 3% of your pensions and savings pots, or a fixed agreed cost. Ongoing advice is usually between 0.5% - 1% of your pots each year.
    </p>
    <p>We have a number of advisers we work with and have checked their advice permissions, costs and processes appear suitable.</p>
    <p>To help with costs the Government also allows people in many pension schemes to withdraw up to £500 tax free from their pension pot to pay towards retirement advice, if your pension provider allows this.</p>

    <template v-if="false || showLowAssetsOffering">
        <p>
            As your total pension and savings pots are below £250,000 it may be harder finding advice as many have minimums above this level.
            However LV= can provide advice for people below this level.
        </p>

        <AdvisorsList :always-display="true" :adviser-keys-filter="['LV']"></AdvisorsList>
    </template>
    <template v-else>
        <p>
            As your total pension and savings pots are above {{ formatter.formatCurrency(lowAssetValueThreshold) }} finding advice, if wanted, should be straightforward. Most people want
            an adviser near them to make meetings etc easier if needed. We work with a range of advisers in all parts of the UK.
        </p>
        <div>
            <dropdown
                :items="regions"
                v-model="selectedRegion"
                :show-label="false"
                classes="form-select form-control mb-3 mb-md-5">
            </dropdown>
        </div>

        <div class="d-flex flex-column flex-md-row gap-4">
            <div class="flex-shrink-0 d-none d-md-block ifa-selector-desktop">
                <template v-for="ifa in qualifyingIFAs" :key="ifa.registrationNumber">
                    <button
                        type="button"
                        class="list-group-item list-group-item-action ifa-list-item text-start text-truncate mb-3 px-0"
                        :class="{ 'active': ifa === selectedIFA }"
                        :title="ifa.name"
                        @click="selectIFA(ifa)"
                    >
                        {{ ifa.abbrevName }}
                    </button>
                    <hr>
                </template>
            </div>
            <div class="d-md-none" v-if="qualifyingIFAs.length > 0">
                <object-dropdown
                    :items="qualifyingIFAs"
                    v-model="selectedIFA"
                    :get-display-text="ifa => ifa.abbrevName"
                    :get-key="ifa => ifa.registrationNumber"
                    class="form-select form-control mb-0"
                />
            </div>
            <div class="flex-grow-1 min-width-0">
                <template v-if="selectedIFA">
                    <div class="border rounded overflow-hidden">
                        <div
                            v-if="selectedIFA.hero"
                            :key="selectedIFA.abbrevName"
                            class="ifa-hero"
                            :class="{ 'ifa-hero-framed': selectedIFA.heroFramed }"
                            :style="selectedIFA.heroContainerStyle">
                            <img
                                :src="getImageSrc(selectedIFA.hero)"
                                :style="selectedIFA.heroStyle"
                                :alt="`${selectedIFA.abbrevName} banner`" />
                        </div>

                        <div class="p-2 p-md-4">
                            <div
                                class="ifa-logo mb-4" :style="selectedIFA.logoContainerStyle"
                                :key="selectedIFA.abbrevName">
                                <img
                                    :src="getCachedImageSrc(selectedIFA.logo)"
                                    class="border rounded"
                                    :class="selectedIFA.logoClass"
                                    :alt="selectedIFA.abbrevName" />
                            </div>
                            <h4 class="fw-bold">{{ selectedIFA.name }}</h4>
                            <hr>
                            <p class="mt-4 fst-italic">"{{ selectedIFA.description }}"</p>
                            <section>
                                <p class="fst-italic mb-1">Initial fees</p>
                                <ul class="list-unstyled ms-3">
                                    <li v-for="(fee, index) in selectedIFA.initialFeesDescription" :key="`initial-${index}`">
                                        {{ fee }}
                                    </li>
                                </ul>
                                <p class="fst-italic mb-1">Ongoing fees</p>
                                <ul class="list-unstyled ms-3 mb-0">
                                    <li v-for="(fee, index) in selectedIFA.ongoingFeesDescription" :key="`ongoing-${index}`">
                                        {{ fee }}
                                    </li>
                                </ul>
                            </section>
                            <div class="ifa-meta mb-3">
                                <div class="ifa-meta-row">
                                    <div class="ifa-meta-label">FCA number:</div>
                                    <div>{{ selectedIFA.registrationNumber }}</div>
                                </div>
                                <div class="ifa-meta-row">
                                    <div class="ifa-meta-label">Website:</div>
                                    <div>
                                        <a :href="selectedIFA.website" class="ifa-website" target="_blank">{{ selectedIFA.website }}</a>
                                        <p class="mb-0 small-text">
                                            If you go on to take advice from {{ selectedIFA.abbrevName }} after being introduced through the
                                            '<span class="fst-italic">Send my plan</span>' option below, {{ selectedIFA.abbrevName }} will pay
                                            Guiide a one-off referral fee. This won't increase the cost of the advice to you.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <hr class="my-4">
                            <p class="fw-bold">Can Guiide help?</p>
                            <p>
                                If you would like us to, we can provide your Guiide plan to {{ selectedIFA.abbrevName }}. If they are able to help, they
                                will contact you directly. Otherwise, we will let you know. We will use the email address registered with Guiide to contact
                                you, unless you'd prefer to provide a phone number below.
                            </p>
                            <p>
                                If they think they can help, they will provide a free, no obligation initial discussion. You can then decide if advice
                                from them is right for you. If you do take advice, we will follow up with you post advice to understand if you
                                were happy with the advice process provided.
                            </p>
                            <template v-if="showSendPlanInputArea">
                                <text-input
                                    ref="requestorNameInput"
                                    v-model="requestorName"
                                    :optional="false"
                                    description="Your name"
                                    maxlength="25"
                                    class="mb-2">
                                </text-input>
                                <text-input
                                    v-model="phoneNumber"
                                    :optional="true"
                                    description="Your phone number"
                                    maxlength="20"
                                    class="mb-3">
                                </text-input>

                                <p class="small-text">
                                    By using the <span class="fst-italic">'{{ sendMyPlanButtonText }}'</span> button, you are consenting to Guiide
                                    sharing your Plan report with {{ selectedIFA.abbrevName }} to consider whether they can provide you with advice.
                                    There is no obligation to take advice or pay any costs to them. Only choose advice with them if you feel they
                                    are the right adviser to meet your retirement planning needs after any initial discussion.
                                </p>

                                <lock-button :text="sendMyPlanButtonText" :async-action="onSendMyPlan"></lock-button>
                            </template>

                            <DynamicAdviceViewer :message="responseMessage" :alert-context="responseOutcome"></DynamicAdviceViewer>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </template>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { ITL1_REGIONS } from '@/guiide/ts/geo/itl';
import { IFA, IFAs } from '@/guiide/ts/ifa/ifa';
import { presentValueOfPensionsAndSavings } from '@/guiide/ts/utilities';
import { formatter } from '@/shared/ts/formatter';
import { app } from '@/guiide/ts/app';
import Dropdown from '../Dropdown.vue';
import ObjectDropdown from '../ObjectDropdown.vue';
import AdvisorsList from '../AdvisorsList.vue';
import TextInput from '../TextInput.vue';
import LockButton from '../LockButton.vue';
import DynamicAdviceViewer from '../DynamicAdviceViewer.vue';

const lowAssetValueThreshold = 250000;
const totalAssetValueAtPresent = computed<number>(() => presentValueOfPensionsAndSavings());
const showLowAssetsOffering = computed<boolean>(() => totalAssetValueAtPresent.value < lowAssetValueThreshold);

const selectedRegionDefault = "Please select your region";
const regions = [ selectedRegionDefault, ...ITL1_REGIONS.map(r => r.name) ];
const selectedRegion = ref<string>(selectedRegionDefault);
const qualifyingIFAs = ref<Array<IFA>>(new Array<IFA>());
const selectedIFA = ref<IFA|null>();
const imageCache = new Map<string, string>();

const requestorNameInput = ref(null);
const requestorName = ref("");
const phoneNumber = ref("");

const showSendPlanInputArea = ref(true);
const responseMessage = ref("");
const responseOutcome = ref("success");

onMounted(async () => {
    await loadImagesIntoCache();
})

watch(
    () => selectedRegion.value,
    () => {
        if (selectedRegion.value == "") {
            qualifyingIFAs.value = new Array<IFA>();
            selectedIFA.value = null;
            return;
        }

        qualifyingIFAs.value = IFAs
            .filter(ifa => ifa.itl1Region.some(region => region.name == selectedRegion.value))
            .sort((a,b) => a.abbrevName.localeCompare(b.abbrevName));
        selectedIFA.value = qualifyingIFAs.value.length > 0 ? qualifyingIFAs.value[0] : null;

        resetSuccessReceipt();
    },
    { immediate: true }
);

const sendMyPlanButtonText = computed<string>(() => `Send my plan to ${selectedIFA.value.abbrevName}`);

function selectIFA(ifa: IFA) {
    selectedIFA.value = ifa;
    resetSuccessReceipt();
}

async function onSendMyPlan() {
    responseMessage.value = "";

    if (!requestorNameInput.value.isValid()) {
        responseMessage.value = "Please tell us your name.";
        responseOutcome.value = "warning";
        return;
    }

    const result = await app.requestIFAContact(selectedIFA.value.abbrevName, requestorName.value, phoneNumber.value);

    showSendPlanInputArea.value = !result.successful;
    responseMessage.value = result.successful
        ? `Thank you for your request. We will contact ${selectedIFA.value.abbrevName} shortly.`
        : result.message;
    responseOutcome.value = result.successful ? "success" : "warning";

    if (result.successful) {
        requestorName.value = "";
        phoneNumber.value = "";
    }
}

function resetSuccessReceipt() {
    responseMessage.value = "";
    showSendPlanInputArea.value = true;
}

async function loadImagesIntoCache() {
    const images = IFAs.flatMap(ifa => [
        ifa.logo,
        ...(ifa.hero ? [ ifa.hero ] : [])
    ]);

    await Promise.all(
        images.map(image => loadImageIntoCache(image))
    );
}

async function loadImageIntoCache(image: string) {
    const src = getImageSrc(image);
    const response = await fetch(src);
    const blob = await response.blob();
    
    const objectUrl = URL.createObjectURL(blob);

    const preloadImage = new Image();
    preloadImage.src = objectUrl;
    await preloadImage.decode();
    
    imageCache.set(image, objectUrl);
}

function getCachedImageSrc(logoName: string): string | undefined {
  return imageCache.get(logoName);
}

const getImageSrc = (logoName: string): string => "content/advisors/" + logoName;

onUnmounted(() => {
    imageCache.forEach(src => URL.revokeObjectURL(src));
    imageCache.clear();
});

</script>

<style scoped>

.ifa-selector-desktop {
    width: 200px;
}

.ifa-list-item.active {
    font-weight: 600;
}

.min-width-0 {
    min-width: 0;
}

.ifa-logo {
  max-width: 180px;
  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;
}

.ifa-logo img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;

  background: white;
}

.ifa-meta {
  display: grid;
  grid-template-columns: 7rem 1fr;
  row-gap: 0.25rem;
  column-gap: 0.75rem;
}

.ifa-meta-row {
  display: contents;
}

.ifa-meta-label {
  color: #6c757d;
}

@media (max-width: 767.98px) {
    .ifa-meta {
        display: block;
    }

    .ifa-meta-row {
        display: block;
        margin-bottom: 1rem;
    }

    .ifa-meta-label {
        margin-bottom: 0.125rem;
    }
}

.small-text {
    font-size: 0.875rem !important;
}

.ifa-hero {
    height: 200px;
    overflow: hidden;
}

.ifa-hero img {
    width: 100%;
    height: 100%;
    display: block;
}

.ifa-hero-framed {
    height: 200px;
    padding: 16px 20px;
    display: flex;
    justify-content: flex-end;
    align-items: stretch;
}

.ifa-hero-framed img {
    width: 45%;
    height: 100%;
    border-radius: 9px;
    object-fit: cover;
}

.ifa-logo-overlap {
    position: relative;
}

.ifa-logo-overlap img {
    background: white;
}

.ifa-website {
    overflow-wrap: anywhere;
}
</style>